import { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useAdminInstructors } from '@/hooks/useAdminInstructors';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface InstructorSelectorProps {
  brandId: string;
  value?: string[] | string | null;
  onChange: (value: string[]) => void;
  multiple?: boolean;
}

export function InstructorSelector({ brandId, value, onChange, multiple = true }: InstructorSelectorProps) {
  const { instructors, isLoading } = useAdminInstructors(brandId);
  const [open, setOpen] = useState(false);

  // Normalize value to array
  const selectedIds = Array.isArray(value) 
    ? value 
    : value 
      ? [value] 
      : [];

  const selectedInstructors = instructors.filter(i => selectedIds.includes(i.id));

  const toggleInstructor = (instructorId: string) => {
    if (multiple) {
      const newValue = selectedIds.includes(instructorId)
        ? selectedIds.filter(id => id !== instructorId)
        : [...selectedIds, instructorId];
      onChange(newValue);
    } else {
      onChange(selectedIds.includes(instructorId) ? [] : [instructorId]);
      setOpen(false);
    }
  };

  const removeInstructor = (instructorId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedIds.filter(id => id !== instructorId));
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading instructors...</div>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between min-h-[42px] h-auto"
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selectedInstructors.length === 0 ? (
              <span className="text-muted-foreground">Select instructor{multiple ? 's' : ''}...</span>
            ) : (
              selectedInstructors.map((instructor) => (
                <Badge
                  key={instructor.id}
                  variant="secondary"
                  className="mr-1 mb-1"
                >
                  <div className="flex items-center gap-1">
                    <Avatar className="h-4 w-4">
                      <AvatarImage src={instructor.image_url || undefined} />
                      <AvatarFallback className="text-[8px]">
                        {instructor.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs">{instructor.name}</span>
                    {multiple && (
                      <button
                        type="button"
                        className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            removeInstructor(instructor.id, e as any);
                          }
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => removeInstructor(instructor.id, e)}
                      >
                        <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                      </button>
                    )}
                  </div>
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[120] pointer-events-auto" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command className="bg-popover">
          <CommandInput placeholder="Search instructors..." className="border-none" />
          <CommandList>
            <CommandEmpty>No instructors found.</CommandEmpty>
            <CommandGroup>
              {instructors.map((instructor) => {
                const isSelected = selectedIds.includes(instructor.id);
                return (
                  <CommandItem
                    key={instructor.id}
                    value={`${instructor.name} ${instructor.title || ''} ${instructor.id}`}
                    onSelect={(currentValue) => {
                      toggleInstructor(instructor.id);
                    }}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex items-center gap-2 flex-1">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={instructor.image_url || undefined} />
                        <AvatarFallback className="text-xs">
                          {instructor.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{instructor.name}</span>
                        {instructor.title && (
                          <span className="text-xs text-muted-foreground">{instructor.title}</span>
                        )}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
